import { addQueryArgs } from '@wordpress/url';
import { __ } from '@wordpress/i18n';
import { useEffect, useState, useMemo, useCallback, useRef } from '@wordpress/element';
// @ts-ignore
// eslint-disable-next-line import/no-unresolved
import { DataViews } from '@dokan/components';
import apiFetch from '@wordpress/api-fetch';
import { useToast } from '@getdokan/dokan-ui';

import { GlobalAddon, AddonListProps } from '../types';

// [FIX] Removed duplicate local declare — dokanProductAddon is now a global
// declared in types.ts via `declare global { const dokanProductAddon: {...} }`

// [FIX] fields moved outside component — no longer recreated on every render
const fields = [
    {
        id: 'name',
        label: __( 'Name', 'dokan' ),
        enableSorting: false,
        render: ( { item }: { item: GlobalAddon } ) => (
            <a
                href={ `${ dokanProductAddon.settingsUrl }?edit=${ item.id }` }
                className="font-bold text-dokan-link hover:underline cursor-pointer relative z-10"
                onClick={ ( e ) => e.stopPropagation() }
            >
                { item.name }
            </a>
        ),
    },
    {
        id: 'priority',
        label: __( 'Priority', 'dokan' ),
        enableSorting: false,
        maxWidth: 100,
        render: ( { item }: { item: GlobalAddon } ) => (
            <span>{ item.priority }</span>
        ),
    },
    {
        id: 'categories',
        label: __( 'Product Categories', 'dokan' ),
        enableSorting: false,
        render: ( { item }: { item: GlobalAddon } ) => {
            if ( item.all_products ) {
                return <span>{ __( 'All Products', 'dokan' ) }</span>;
            }

            if ( ! item.categories.length ) {
                return <span>&mdash;</span>;
            }

            return (
                <span>
                    { item.categories
                        .map( ( cat ) => cat.name )
                        .join( ', ' ) }
                </span>
            );
        },
    },
    {
        id: 'field_count',
        label: __( 'Number of Fields', 'dokan' ),
        enableSorting: false,
        maxWidth: 140,
        render: ( { item }: { item: GlobalAddon } ) => (
            <span>{ item.field_count }</span>
        ),
    },
];

// [FIX] Correct initial fields list — filter out 'name' to avoid empty string entry
const defaultViewFields = fields.map( ( field ) => field.id );

const AddonList = ( { navigate }: AddonListProps ) => {
    const [ data, setData ] = useState< GlobalAddon[] >( [] );
    const [ isLoading, setIsLoading ] = useState( true );
    const [ totalItems, setTotalItems ] = useState( 0 );
    const [ view, setView ] = useState( {
        perPage: 10,
        page: 1,
        type: 'table',
        fields: defaultViewFields,
        layout: {
            styles: {
                name: { width: '40%' },
                priority: { width: '15%' },
                categories: { width: '30%' },
                field_count: { width: '15%' },
            },
        },
    } );

    // [FIX] Use a ref to always access latest view values inside callbacks
    // without adding view to dependency arrays
    const viewRef = useRef( view );
    viewRef.current = view;

    const toast = useToast();

    const fetchAddons = useCallback( async () => {
        setIsLoading( true );
        try {
            const response = ( await apiFetch( {
                path: addQueryArgs( '/dokan/v1/vendor/product-addons', {
                    per_page: viewRef.current.perPage,
                    page: viewRef.current.page,
                } ),
                method: 'GET',
                parse: false,
            } ) ) as Response;

            const addons = await response.json();
            const total = parseInt(
                response.headers.get( 'X-WP-Total' ) ?? '0'
            );

            setData( addons );
            setTotalItems( total );
        } catch ( error: any ) {
            toast( {
                type: 'error',
                title:
                    error.message ||
                    __( 'Failed to fetch product addons', 'dokan' ),
            } );
        } finally {
            setIsLoading( false );
        }
    }, [ toast ] ); // [FIX] No longer depends on view.page/view.perPage — reads via ref

    const actions = useMemo(
        () => [
            {
                id: 'addon-edit',
                label: () => __( 'Edit', 'dokan' ),
                callback: ( [ item ]: GlobalAddon[] ) => {
                    window.location.href = `${ dokanProductAddon.settingsUrl }?edit=${ item.id }`;
                },
            },
            {
                id: 'addon-delete',
                isDestructive: true,
                label: () => __( 'Delete', 'dokan' ),
                callback: async ( [ item ]: GlobalAddon[] ) => {
                    try {
                        await apiFetch( {
                            path: `/dokan/v1/vendor/product-addons/${ item.id }`,
                            method: 'DELETE',
                        } );

                        toast( {
                            type: 'success',
                            title: __(
                                'Product addon deleted successfully',
                                'dokan'
                            ),
                        } );

                        await fetchAddons();
                    } catch ( error: any ) {
                        toast( {
                            type: 'error',
                            title:
                                error.message ||
                                __(
                                    'Failed to delete product addon',
                                    'dokan'
                                ),
                        } );
                    }
                },
            },
        ],
        [ fetchAddons, navigate, toast ]
    );

    // [FIX] Stable initial fetch — useEffect has no deps, runs once on mount
    useEffect( () => {
        void fetchAddons();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [] );

    const onViewChange = useCallback( ( newView: typeof view ) => {
        setView( newView );
        // [FIX] Trigger fetch directly on pagination change — avoids dep chain
        if (
            newView.page !== viewRef.current.page ||
            newView.perPage !== viewRef.current.perPage
        ) {
            viewRef.current = newView;
            void fetchAddons();
        }
    }, [ fetchAddons ] );

    return (
        <DataViews
            namespace="dokan-product-addon-data-view"
            data={ data }
            fields={ fields }
            view={ view }
            onChangeView={ onViewChange }
            getItemId={ ( item: GlobalAddon ) => item.id }
            isLoading={ isLoading }
            paginationInfo={ {
                totalItems,
                totalPages: Math.ceil( totalItems / view.perPage ),
            } }
            actions={ actions }
        />
    );
};

export default AddonList;
